/* Frontend Replay model. Chart components consume this shape — the API
   transform layer (../api/transform.ts) maps backend DTOs into it. */

export type Tier = 'S' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

export interface Tick {
  t: number;
  x: number; y: number; z: number;
  speed: number;
  steer: number;
  gas: boolean;
  brake: boolean;
  gear: number;
  rpm: number;
  pitch: number; yaw: number; roll: number;
  vx: number; vy: number; vz: number;
  vmag: number;
}

export interface Checkpoint {
  t: number;
  sIndex: number;
  s: number;
}

export interface ExactBin {
  value: number;
  count: number;
  isSuspicious: boolean;
}

export interface Cluster {
  tStart: number;
  tEnd: number;
  value: number;
  count: number;
}

export interface ReplayData {
  ticks: Tick[];
  checkpoints: Checkpoint[];
  duration: number;
  topSpeed: number;
  avgSpeed: number;
  bins: number[];
  exactBins: ExactBin[];
  clusters: Cluster[];
}

export interface ReplayMeta {
  replayUid: string;
  ghostUid: string;
  challengeUid: string;
  author: string;
  location: string;
  map: string;
  respawns: number;
  tier: Tier;
  scanDate: string;
  raceTimeMS: number;
  raceTimeStr: string;
  topSpeed: number;
  topSpeedKmh: number;
  avgSpeed: number;
  avgSpeedKmh: number;
  suspiciousCount: number;
}

export interface Replay {
  meta: ReplayMeta;
  data: ReplayData;
}

// ============ Formatters ============
export function formatTime(ms: number): string {
  const t = Math.max(0, ms);
  const m = Math.floor(t / 60000);
  const s = Math.floor((t % 60000) / 1000);
  const mm = Math.floor(t % 1000);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(mm).padStart(3, '0')}`;
}

export function kmh(speed: number): number {
  return speed * 3.6;
}

// Human-readable trailing segment of a challenge UID (e.g. "A01-Race").
export function shortMapName(challengeUid: string | null | undefined): string {
  if (!challengeUid) return '—';
  const parts = challengeUid.split(/[\\/]/);
  return parts[parts.length - 1] || challengeUid;
}

// ============ Helpers ============
export function sampleAt(replay: Replay, tNorm: number): Tick {
  const ticks = replay.data.ticks;
  if (!ticks.length) {
    return {
      t: 0, x: 0, y: 0, z: 0,
      speed: 0, steer: 0,
      gas: false, brake: false,
      gear: 0, rpm: 0,
      pitch: 0, yaw: 0, roll: 0,
      vx: 0, vy: 0, vz: 0, vmag: 0,
    };
  }
  const tSec = tNorm * replay.data.duration;
  let lo = 0, hi = ticks.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ticks[mid].t < tSec) lo = mid + 1; else hi = mid;
  }
  return ticks[lo];
}

// Bounds of one or more replays' racing lines in world coords. Used by the
// minimap to fit-contain. Returns sensible defaults when the input is empty.
export interface Bounds { minX: number; maxX: number; minZ: number; maxZ: number; }

export function computeBounds(replays: Replay[] | Replay): Bounds {
  const arr = Array.isArray(replays) ? replays : [replays];
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const r of arr) {
    for (const tk of r.data.ticks) {
      if (tk.x < minX) minX = tk.x;
      if (tk.x > maxX) maxX = tk.x;
      if (tk.z < minZ) minZ = tk.z;
      if (tk.z > maxZ) maxZ = tk.z;
    }
  }
  if (!isFinite(minX)) {
    minX = -1; maxX = 1; minZ = -1; maxZ = 1;
  }
  return { minX, maxX, minZ, maxZ };
}

// ============ Color mappers ============
const SPEED_RAMP: [number, number, number][] = [
  [26, 78, 138],
  [0, 153, 255],
  [0, 212, 196],
  [123, 240, 107],
  [255, 225, 74],
  [255, 138, 60],
  [255, 51, 85],
];

export function speedColor(speed: number, topSpeed = 95): string {
  const t = Math.max(0, Math.min(1, speed / (topSpeed || 1)));
  const idx = t * (SPEED_RAMP.length - 1);
  const i0 = Math.floor(idx);
  const i1 = Math.min(SPEED_RAMP.length - 1, i0 + 1);
  const f = idx - i0;
  const c = SPEED_RAMP[i0].map((v, k) => Math.round(v + (SPEED_RAMP[i1][k] - v) * f));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

export function tierColor(tier: Tier): string {
  const m: Record<Tier, string> = {
    S: 'var(--tier-s)', A: 'var(--tier-a)', B: 'var(--tier-b)',
    C: 'var(--tier-c)', D: 'var(--tier-d)', E: 'var(--tier-e)', F: 'var(--tier-f)',
  };
  return m[tier] || 'var(--fg-3)';
}
